import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import SearchableSelect from '../../searchable-select';

/** Maps metric type keys to display labels for the card header. */
const METRIC_TYPE_DISPLAY = {
  email_opened: 'EMAIL OPEN RATE',
  email_replied: 'EMAIL RESPONSE RATE',
  interaction_created: 'INTERACTION CREATED',
  gift_given: 'GIFT GIVEN',
  portfolio_stage_reached: 'PORTFOLIO STAGE REACHED',
  saved_search: 'SAVED SEARCH QUALIFICATION',
  email_clicked: 'EMAIL CLICK RATE',
  email_bounced: 'EMAIL BOUNCED',
  unsubscribed: 'UNSUBSCRIBED',
  gifts_amount: 'GIFTS OVER AMOUNT',
  gifts_count: 'NUMBER OF GIFTS',
  meetings_count: 'NUMBER OF MEETINGS',
  proposal_funded: 'PROPOSAL FUNDED',
};

/**
 * A single madlib-style success metric card.
 * Renders a sentence with inline inputs that configure the metric.
 */
const SuccessMetricCard = ({
  metric,
  onChange,
  onRemove,
  interactionTypes = [],
  savedSearches = [],
  portfolioTeams = {},
}) => {
  const { type, targetPercentage, config = {} } = metric;
  const { getStages, loading: teamsLoading } = portfolioTeams;
  const [stages, setStages] = useState([]);

  // Load stages when team changes for portfolio_stage_reached.
  // teamsLoading in deps ensures the effect re-fires after teams arrive from the API,
  // since getStages is a stable callback that reads a ref (won't change reference).
  useEffect(() => {
    if (type === 'portfolio_stage_reached' && config.teamId && getStages) {
      getStages(config.teamId).then((s) => setStages(s || []));
    } else {
      setStages([]);
    }
  }, [type, config.teamId, getStages, teamsLoading]);

  const [editingTarget, setEditingTarget] = useState(null);

  const commitTarget = (value) => {
    setEditingTarget(null);
    const num = value === '' || Number(value) < 1 ? 1 : Math.min(100, Number(value));
    onChange({ ...metric, targetPercentage: num });
  };

  const updateConfig = (key, value) => {
    onChange({ ...metric, config: { ...config, [key]: value } });
  };

  const renderPercentInput = () => (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className="success-metric-card--inline-input"
      value={editingTarget != null ? editingTarget : targetPercentage}
      onChange={(e) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        setEditingTarget(val);
      }}
      onBlur={(e) => commitTarget(e.target.value)}
      aria-label="Target percentage"
    />
  );

  const renderBody = () => {
    switch (type) {
      case 'email_opened':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents <strong>open at least one email</strong>, this cadence is
            successful.
          </p>
        );

      case 'email_replied':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents <strong>reply to at least one email</strong>, this cadence is
            successful.
          </p>
        );

      case 'interaction_created':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents have an interaction of{' '}
            <select
              className="success-metric-card--inline-select"
              value={config.interactionType || ''}
              onChange={(e) => updateConfig('interactionType', e.target.value || null)}
              aria-label="Interaction type"
            >
              {interactionTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>{' '}
            logged during the cadence, this cadence is successful.
          </p>
        );

      case 'gift_given':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents make a gift of ${' '}
            <input
              type="text"
              inputMode="decimal"
              className="success-metric-card--inline-input success-metric-card--inline-input-wide"
              value={config.minAmount || ''}
              onChange={(e) => updateConfig('minAmount', e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder="e.g. 1,000"
              aria-label="Minimum gift amount"
            />{' '}
            or more to{' '}
            <input
              type="text"
              className="success-metric-card--inline-input success-metric-card--inline-input-wide"
              value={config.designation || ''}
              onChange={(e) => updateConfig('designation', e.target.value)}
              placeholder="any designation"
              aria-label="Gift designation"
            />
            , this cadence is successful.
          </p>
        );

      case 'portfolio_stage_reached':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents assigned to{' '}
            <select
              className="success-metric-card--inline-select"
              value={String(config.teamId || '')}
              onChange={(e) => {
                const team = (portfolioTeams.teams || []).find((t) => String(t.id) === e.target.value);
                onChange({
                  ...metric,
                  config: {
                    ...config,
                    teamId: e.target.value || null,
                    teamName: team?.name || null,
                    stageId: null,
                    stageName: null,
                  },
                });
              }}
              aria-label="Portfolio team"
            >
              <option value="">pick a team</option>
              {(portfolioTeams.teams || []).map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>{' '}
            reach the{' '}
            <select
              className="success-metric-card--inline-select"
              value={String(config.stageId || '')}
              onChange={(e) => {
                const stage = stages.find((s) => String(s.id) === e.target.value);
                onChange({
                  ...metric,
                  config: { ...config, stageId: e.target.value || null, stageName: stage?.name || null },
                });
              }}
              disabled={!config.teamId}
              aria-label="Portfolio stage"
            >
              <option value="">pick a stage</option>
              {stages.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>{' '}
            stage, this cadence is successful.
          </p>
        );

      case 'saved_search':
        return (
          <div className="success-metric-card--sentence-block">
            <p className="success-metric-card--sentence">
              If {renderPercentInput()}% of constituents qualify for the saved search below, this cadence is successful.
            </p>
            <SearchableSelect
              options={savedSearches}
              value={savedSearches.find((s) => s.value === config.savedSearchId) || null}
              onChange={(option) => {
                onChange({
                  ...metric,
                  config: {
                    ...config,
                    savedSearchId: option?.value || null,
                    savedSearchName: option?.label || null,
                    savedSearchListId: option?.listId ?? null,
                  },
                });
              }}
              placeholder="Search saved searches..."
              className="success-metric-card--searchable-dropdown"
            />
          </div>
        );

      case 'email_clicked':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents <strong>click a link in at least one email</strong>, this cadence
            is successful.
          </p>
        );

      case 'email_bounced':
        return (
          <p className="success-metric-card--sentence">
            Constituents are flagged when their email <strong>bounces</strong> during the cadence.
          </p>
        );

      case 'unsubscribed':
        return (
          <p className="success-metric-card--sentence">
            Constituents are flagged when they <strong>unsubscribe</strong> during the cadence.
          </p>
        );

      case 'gifts_amount':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents give a total of ${' '}
            <input
              type="text"
              inputMode="decimal"
              className="success-metric-card--inline-input success-metric-card--inline-input-wide"
              value={config.threshold || ''}
              onChange={(e) => updateConfig('threshold', e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder="e.g. 500"
              aria-label="Gift amount threshold"
            />{' '}
            or more, this cadence is successful.
          </p>
        );

      case 'gifts_count':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents make{' '}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="success-metric-card--inline-input"
              value={config.threshold || ''}
              onChange={(e) => updateConfig('threshold', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 2"
              aria-label="Gift count threshold"
            />{' '}
            or more gifts, this cadence is successful.
          </p>
        );

      case 'meetings_count':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents have{' '}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="success-metric-card--inline-input"
              value={config.threshold || ''}
              onChange={(e) => updateConfig('threshold', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 1"
              aria-label="Meeting count threshold"
            />{' '}
            or more meetings, this cadence is successful.
          </p>
        );

      case 'proposal_funded':
        return (
          <p className="success-metric-card--sentence">
            If {renderPercentInput()}% of constituents have a <strong>proposal funded</strong>, this cadence is
            successful.
          </p>
        );

      default:
        return <p className="success-metric-card--sentence">Unknown metric type: {type}</p>;
    }
  };

  return (
    <div className="success-metric-card">
      <div className="success-metric-card--header">
        <span className="success-metric-card--type-label">{METRIC_TYPE_DISPLAY[type] || type}</span>
        <div className="success-metric-card--header-right">
          <button type="button" className="success-metric-card--remove" onClick={onRemove} aria-label="Remove metric">
            <Icon icon="close" />
          </button>
        </div>
      </div>
      <div className="success-metric-card--body">{renderBody()}</div>
    </div>
  );
};

SuccessMetricCard.propTypes = {
  metric: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    targetPercentage: PropTypes.number.isRequired,
    config: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  interactionTypes: PropTypes.array,
  savedSearches: PropTypes.array,
  portfolioTeams: PropTypes.object,
};

export default SuccessMetricCard;
